'use client'

import React, { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useRestorePlaybackOnReload } from '@/hooks/useRestorePlaybackOnReload'
import { attachHlsSource } from '@/lib/hlsPlayer'
import VideoCanvasPresentation from './VideoCanvasPresentation'

export interface VideoCanvasProps {
  videoUrl: string
  /** HLSマスタープレイリストURL。READY時のみ。指定時はHLS優先、非対応/失敗時はvideoUrl(MP4)にフォールバック */
  hlsUrl?: string | null
  isPlaying: boolean
  isMuted: boolean
  /** 音量（0〜1）。未指定は 1 */
  volume?: number
  autoplay?: boolean
  playbackRate?: number
  onPlayPause: () => void
  onTimeUpdate: (currentTime: number, duration: number) => void
  onEnded: () => void
  videoRef: RefObject<HTMLVideoElement | null>
  /** 枠に対する動画の収め方。既定 `cover`。ワイド型は `contain`（ADR-0016） */
  fit?: 'cover' | 'contain'
  /** 終了時のリプレイ用オーバーレイを出さない（分岐ボタン・終了画面を上に出す場合） */
  suppressEndedOverlay?: boolean
  /** 音あり自動再生がブロックされ、ミュートに落として再生を続けたときに呼ぶ（親のミュートUI同期用） */
  onAutoplayMuted?: () => void
  /**
   * この動画を指す不変のID（ノードキーなど）。
   *
   * 「同じ動画か」の判定に videoUrl を使うと、署名付きURLが再取得で付け替わったときに
   * 別の動画とみなされ、再生位置の復元と自動再生の抑止がどちらも外れる（別タブから
   * 戻ると 0:00 から再生される事象の原因）。渡せる場合は必ず渡すこと。
   */
  mediaId?: string
}

const VideoCanvas: FC<VideoCanvasProps> = ({
  videoUrl,
  hlsUrl,
  isPlaying,
  isMuted,
  volume = 1,
  autoplay = false,
  playbackRate = 1.0,
  onPlayPause,
  onTimeUpdate,
  onEnded,
  videoRef,
  fit,
  suppressEndedOverlay,
  onAutoplayMuted,
  mediaId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [showPlayButton, setShowPlayButton] = useState(false)
  const [isEnded, setIsEnded] = useState(false)

  // 別タブから戻ってメディアが読み込み直されても、止めた位置・止めた状態のまま戻す。
  // 判定キーは URL ではなく不変のID。hls.js の blob: URL も署名付きURLも読み込み直しで
  // 変わるため、URL を鍵にすると「同じ動画」と判定できない。
  const playbackKey = mediaId ?? videoUrl
  useRestorePlaybackOnReload(videoRef, { mediaKey: playbackKey })
  /** マウント起点の自動再生を済ませた動画。読み込み直しで再生し直さないためのガード */
  const autoPlayedKeyRef = useRef<string | null>(null)

  // 音量は muted と違って属性で渡せないので、要素に直接当てる（次の動画へ進んでも同じ音量にする）
  useEffect(() => {
    const video = videoRef.current
    if (video) video.volume = volume
  }, [videoRef, volume, videoUrl])

  // Handle video metadata loading
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadedData = () => {
      setIsLoading(false)
      setHasError(false)
      // 動画が読み込まれたら再生速度を設定
      video.playbackRate = playbackRate
    }

    const handleCanPlay = () => {
      setIsLoading(false)
      // 再生可能になったらも再生速度を確実に設定
      video.playbackRate = playbackRate
    }

    const handleError = () => {
      setIsLoading(false)
      setHasError(true)
      console.error('Video loading error:', videoUrl)
    }

    const handleTimeUpdate = () => {
      if (video.currentTime && video.duration) {
        onTimeUpdate(video.currentTime, video.duration)
      }
    }

    const handleEnded = () => {
      setIsEnded(true)
      setShowPlayButton(false)
      onEnded()
    }

    const handlePlay = () => {
      // 動画が再生されたら再生ボタンを非表示、終了状態をリセット
      setShowPlayButton(false)
      setIsEnded(false)
    }

    const handlePause = () => {
      // 動画が一時停止されたら再生ボタンを表示
      setShowPlayButton(true)
    }

    video.addEventListener('loadeddata', handleLoadedData)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('error', handleError)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('error', handleError)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
    }
  }, [videoRef, videoUrl, onTimeUpdate, onEnded, playbackRate, autoplay])

  // Handle playback rate changes
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.playbackRate = playbackRate
  }, [videoRef, playbackRate, videoUrl])

  // Attach HLS source when available (native HLS / hls.js / MP4 fallback)
  useEffect(() => {
    const video = videoRef.current
    if (!video || !hlsUrl) return

    const cleanup = attachHlsSource(video, { hlsUrl, mp4Url: videoUrl, autoPlay: autoplay })
    return cleanup
  }, [videoRef, hlsUrl, videoUrl, autoplay])

  // Handle autoplay with user interaction requirement
  useEffect(() => {
    const video = videoRef.current
    if (!video || !autoplay) return
    // この動画の自動再生は一度きり。別タブから戻って読み込み直されても、
    // 視聴者が止めた動画を 0:00 から再生し直さない。
    if (autoPlayedKeyRef.current === playbackKey) return

    const attemptAutoplay = async () => {
      autoPlayedKeyRef.current = playbackKey
      try {
        await video.play()
        // 自動再生が成功したら再生ボタンを非表示
        setShowPlayButton(false)
      } catch {
        // 音あり自動再生（BOX/WIDE のクリック起点再生）がポリシーでブロックされた場合は、
        // 黒画面で止めずにミュートへ落として再生を続ける。親へ通知してミュートUIも同期する。
        if (!video.muted) {
          try {
            video.muted = true
            onAutoplayMuted?.()
            await video.play()
            setShowPlayButton(false)
            return
          } catch {
            // ミュートでも再生できなければ下の再生ボタン表示に落ちる
          }
        }
        // 自動再生が失敗したら再生ボタンを表示
        setShowPlayButton(true)
      }
    }

    if (video.readyState >= 3) {
      attemptAutoplay()
    } else {
      video.addEventListener('canplay', attemptAutoplay, { once: true })
      return () => {
        video.removeEventListener('canplay', attemptAutoplay)
      }
    }
    // videoUrl 依存: ノード遷移で src が変わるたびに play() を明示的に試みる。
    // 属性 autoplay 任せだと音あり再生（BOX/WIDE）がポリシーでブロックされたとき
    // 何も起きず止まり、ミュートフォールバックにも入れない。
  }, [videoRef, autoplay, playbackKey, videoUrl, onAutoplayMuted])

  // Handle replay from beginning
  const handleReplay = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    video.currentTime = 0
    video.play()
    setIsEnded(false)
  }, [videoRef])

  // Handle click for play/pause
  const handleClick = useCallback(() => {
    onPlayPause()
  }, [onPlayPause])

  // Handle touch for mobile
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault()
      onPlayPause()
    },
    [onPlayPause],
  )

  return (
    <VideoCanvasPresentation
      videoUrl={videoUrl}
      hlsUrl={hlsUrl}
      isPlaying={isPlaying}
      isMuted={isMuted}
      playbackRate={playbackRate}
      isLoading={isLoading}
      hasError={hasError}
      showPlayButton={showPlayButton}
      isEnded={isEnded}
      onClick={handleClick}
      onTouchEnd={handleTouchEnd}
      onReplay={handleReplay}
      videoRef={videoRef}
      fit={fit}
      suppressEndedOverlay={suppressEndedOverlay}
    />
  )
}

export default VideoCanvas
